import React, { useState, useEffect, useMemo } from 'react';
import { 
  Ticket, 
  CheckCircle, 
  Clock, 
  AlertTriangle,
  Users,
  TrendingUp,
  BarChart3,
  Activity,
  Shield,
  Target,
  ArrowRight,
  X,
  Zap,
  RefreshCw
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import Chart from '../../components/ui/Chart';
import { getDashboardMetrics, getDrillDownData } from '../../api/dashboardService';
import toast from 'react-hot-toast';

const PublicDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [kpis, setKpis] = useState({
    totalTickets: 0,
    openTickets: 0,
    resolvedTickets: 0,
    closedTickets: 0,
    inProgressTickets: 0,
    slaCompliance: 0,
    avgResolutionTime: '0h 0m',
    activeAgents: 0,
    ticketsToday: 0
  });
  const [drilldownData, setDrilldownData] = useState(null);
  const [selectedKpi, setSelectedKpi] = useState(null);
  const [dateRange, setDateRange] = useState('30d');
  const [ticketTrends, setTicketTrends] = useState(null);
  const [performanceMetrics, setPerformanceMetrics] = useState(null);

  useEffect(() => {
    loadDashboardData();
  }, [dateRange]);

  const getDaysFromRange = (range) => {
    const rangeMap = {
      '7d': 7,
      '30d': 30,
      '90d': 90,
      '1y': 365
    };
    return rangeMap[range] || 30;
  };

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      
      const days = getDaysFromRange(dateRange);

      // Load real data from API
      const metrics = await getDashboardMetrics(days);

      if (metrics) {
        // Map API response to component state
        setKpis({
          totalTickets: metrics.totalTickets || 0,
          openTickets: metrics.openTickets || 0,
          resolvedTickets: metrics.resolvedTickets || 0,
          closedTickets: metrics.closedTickets || 0,
          inProgressTickets: metrics.inProgressTickets || 0,
          slaCompliance: metrics.slaCompliance || 100,
          avgResolutionTime: metrics.avgResolutionTime || '0m',
          activeAgents: metrics.activeAgents || 0,
          ticketsToday: metrics.ticketsToday || 0
        });

        // Store trends and performance metrics for drill-down
        if (metrics.ticketTrends) {
          setTicketTrends(metrics.ticketTrends);
        }
        if (metrics.performanceMetrics) {
          setPerformanceMetrics(metrics.performanceMetrics);
        }
      } else {
        // If no data returned, show error
        toast.error('No dashboard data available');
      }

    } catch (error) {
      console.error('Error loading dashboard data:', error);
      const errorMessage = error?.response?.status === 404
        ? 'Dashboard service not available'
        : error?.response?.status === 400
          ? 'Invalid request. Please check your parameters.'
          : error?.response?.status === 500
            ? 'Server error. Please try again later.'
            : error?.message || 'Failed to load dashboard data';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleKpiClick = async (kpiType) => {
    try {
      setSelectedKpi(kpiType);
      
      const days = getDaysFromRange(dateRange);
      let endDate = new Date();
      let startDate = new Date();
      startDate.setDate(startDate.getDate() - days);

      // For Tickets Today, restrict to the current calendar date only
      if (kpiType === 'ticketsToday') {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        startDate = new Date(today);
        endDate = new Date(today);
      }

      // Map KPI types to metric types for API
      const metricTypeMap = {
        totalTickets: 'status',      // all tickets within date range
        openTickets: 'open',         // only open tickets
        resolvedTickets: 'resolved', // only resolved/closed tickets
        closedTickets: 'closed',     // only closed tickets
        inProgressTickets: 'inprogress',
        slaCompliance: 'resolved',   // resolved tickets for SLA analysis
        avgResolutionTime: 'resolved', // resolved tickets for resolution time
        ticketsToday: 'date',        // tickets created today (by date range)
        activeAgents: 'status'       // all tickets, frontend groups by agent
      };

      // Default tenant ID for public dashboard (no authentication required)
      const DEFAULT_TENANT_ID = 1;

      const drillDownRequest = {
        metricType: metricTypeMap[kpiType] || 'status',
        startDate: startDate.toISOString().split('T')[0],
        endDate: endDate.toISOString().split('T')[0],
        tenantId: DEFAULT_TENANT_ID, // Always send default tenant ID for public dashboard
        limit: 100, // Default limit for drill-down lists
        offset: 0
      };

      const apiData = await getDrillDownData(drillDownRequest);

      if (apiData && apiData.tickets) {
        // Get title based on KPI type
        const titleMap = {
          'totalTickets': 'Total Tickets Breakdown',
          'openTickets': 'Open Tickets',
          'resolvedTickets': 'Resolved Tickets',
          'closedTickets': 'Closed Tickets',
          'inProgressTickets': 'In Progress Tickets',
          'slaCompliance': 'SLA Compliance Tickets',
          'avgResolutionTime': 'Tickets by Resolution Time',
          'ticketsToday': 'Tickets Created Today',
          'activeAgents': 'Agent Tickets'
        };

        const drilldown = {
          title: titleMap[kpiType] || `${kpiType} Details`,
            data: {
            tickets: apiData.tickets || [],
            totalCount: apiData.totalCount || 0,
            byStatus: apiData.byStatus ? Object.entries(apiData.byStatus).map(([status, count]) => ({ status, count })) : [],
            byPriority: apiData.byPriority ? Object.entries(apiData.byPriority).map(([priority, count]) => ({ priority, count })) : [],
            byCategory: apiData.byCategory ? Object.entries(apiData.byCategory).map(([category, count]) => ({ category, count })) : [],
            byDepartment: apiData.byDepartment ? Object.entries(apiData.byDepartment).map(([department, count]) => ({ department, count })) : []
          }
        };
      
      setDrilldownData(drilldown);
      } else {
        toast.error('No tickets found');
      }
    } catch (error) {
      console.error('Error loading drilldown data:', error);
      const errorMessage = error?.response?.status === 404
        ? 'Drill-down service not available'
        : error?.response?.status === 400
          ? 'Invalid request. Please check your parameters.'
          : error?.response?.status === 500
            ? 'Server error. Please try again later.'
            : error?.message || 'Failed to load detailed data';
      toast.error(errorMessage);
    }
  };

  const closeDrilldown = () => {
    setDrilldownData(null);
    setSelectedKpi(null);
  };

  const kpiCards = [
    {
      id: 'totalTickets',
      title: 'Total Tickets',
      value: kpis.totalTickets,
      icon: Ticket,
      color: 'blue',
      description: 'All tickets in the system'
    },
    {
      id: 'openTickets',
      title: 'Open Tickets',
      value: kpis.openTickets,
      icon: Ticket,
      color: 'orange',
      description: 'Tickets awaiting resolution'
    },
    {
      id: 'resolvedTickets',
      title: 'Resolved Tickets',
      value: kpis.resolvedTickets,
      icon: CheckCircle,
      color: 'green',
      description: 'Successfully resolved tickets'
    },
    {
      id: 'closedTickets',
      title: 'Closed Tickets',
      value: kpis.closedTickets,
      icon: CheckCircle,
      color: 'indigo',
      description: 'Completed and closed tickets'
    },
    {
      id: 'inProgressTickets',
      title: 'In Progress',
      value: kpis.inProgressTickets,
      icon: Activity,
      color: 'purple',
      description: 'Tickets currently being worked on'
    },
    {
      id: 'slaCompliance',
      title: 'SLA Compliance',
      value: `${kpis.slaCompliance}%`,
      icon: Shield,
      color: 'indigo',
      description: 'Service level agreement compliance rate'
    },
    {
      id: 'avgResolutionTime',
      title: 'Avg Resolution Time',
      value: kpis.avgResolutionTime,
      icon: Clock,
      color: 'teal',
      description: 'Average time to resolve tickets'
    },
    {
      id: 'ticketsToday',
      title: 'Tickets Today',
      value: kpis.ticketsToday,
      icon: TrendingUp,
      color: 'pink',
      description: 'New tickets created today'
    },
    {
      id: 'activeAgents',
      title: 'Active Agents',
      value: kpis.activeAgents,
      icon: Users,
      color: 'cyan',
      description: 'Currently active support agents'
    }
  ];

  // Helper function to get unique status badge colors
  const getStatusBadgeColor = (statusCode) => {
    if (!statusCode) return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';

    const normalized = statusCode.toLowerCase().trim();

    switch (normalized) {
      case 'open':
        return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400 border border-emerald-300 dark:border-emerald-700';
      case 'new':
        return 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-400 border border-cyan-300 dark:border-cyan-700';
      case 'closed':
        return 'bg-slate-100 text-slate-800 dark:bg-slate-700 dark:text-slate-300 border border-slate-300 dark:border-slate-600';
      case 'resolved':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400 border border-blue-300 dark:border-blue-700';
      case 'in_progress':
      case 'inprogress':
        return 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400 border border-amber-300 dark:border-amber-700';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400 border border-yellow-300 dark:border-yellow-700';
      case 'escalated':
        return 'bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-400 border border-rose-300 dark:border-rose-700';
      case 'on_hold':
      case 'onhold':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400 border border-purple-300 dark:border-purple-700';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600';
    }
  };

  // Helper function to get unique priority badge colors
  const getPriorityBadgeColor = (priorityCode) => {
    if (!priorityCode) return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';

    const normalized = priorityCode.toLowerCase().trim();

    switch (normalized) {
      case 'low':
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400 border border-green-300 dark:border-green-700';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400 border border-yellow-300 dark:border-yellow-700';
      case 'high':
        return 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400 border border-orange-300 dark:border-orange-700';
      case 'urgent':
        return 'bg-red-800 text-red-50 dark:bg-red-950 dark:text-red-100 border border-red-700 dark:border-red-800';
      case 'critical':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400 border border-purple-300 dark:border-purple-700';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div>
              <h1 className="text-xl font-bold text-gray-900 dark:text-white">HelpDesk Dashboard</h1>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">
                Real time insights and key performance indicators
              </p>
            </div>
            <div className="flex items-center gap-2">
              <select
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value)}
                className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-xs font-medium h-7"
              >
                <option value="7d">Last 7 days</option>
                <option value="30d">Last 30 days</option>
                <option value="90d">Last 90 days</option>
                <option value="1y">Last year</option>
              </select>
              <Button
                onClick={() => loadDashboardData()}
                variant="outline"
                className="flex items-center gap-1 text-xs py-1 px-2 h-7"
              >
                <RefreshCw className="h-3 w-3" />
                Refresh
              </Button>
              <Button
              onClick = {()=>
                window.open("https://app.powerbi.com/view?r=eyJrIjoiY2VjYTE0OTctZDIzMy00Njg5LThlMTYtYTgwMzFhZDBkZWI3IiwidCI6ImQwOThhNTc2LWJmM2MtNDY3NC04ZjRiLWYxYWU0NjUzYTlmNCJ9")
              }
               className="text-xs py-1 px-2 h-7"
              >
                Analytics
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* KPI Cards - Top Row */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {kpiCards.slice(0, 4).map((kpi, index) => {
            const Icon = kpi.icon;
            const isOpenTickets = kpi.id === 'openTickets';
            
            return (
              <Card
                key={kpi.id}
                className={`cursor-pointer hover:shadow-lg transition-all duration-200 ${isOpenTickets ? 'border-2 border-green-500 shadow-md' : 'hover:border-blue-300 dark:hover:border-blue-600'
                  }`}
                onClick={() => handleKpiClick(kpi.id)}
              >
                <CardContent className="p-3">
                  <div className="flex items-center justify-between mb-2">
                    <div className={`p-1.5 rounded-lg ${kpi.color === 'blue' ? 'bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400' :
                        kpi.color === 'orange' ? 'bg-orange-100 dark:bg-orange-900 text-orange-600 dark:text-orange-400' :
                          kpi.color === 'green' ? 'bg-green-100 dark:bg-green-900 text-green-600 dark:text-green-400' :
                            kpi.color === 'purple' ? 'bg-purple-100 dark:bg-purple-900 text-purple-600 dark:text-purple-400' :
                              'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                      }`}>
                      <Icon className="h-3 w-3" />
                    </div>
                    <TrendingUp className="h-2.5 w-2.5 text-green-500" />
                  </div>
                  <h3 className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-0.5">
                    {kpi.title}
                  </h3>
                  <p className="text-xl font-bold text-gray-900 dark:text-white mb-0.5">
                    {typeof kpi.value === 'number' ? kpi.value.toLocaleString() : kpi.value}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 leading-tight">
                    {kpi.description}
                  </p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Middle Row - Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
          {/* Left: Ticket Trends Area Chart */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base font-semibold">
                Ticket Trends - Last 30 Days
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              {ticketTrends?.timeSeries && ticketTrends.timeSeries.length > 0 ? (
                <Chart
                  type="area"
                  data={ticketTrends.timeSeries.map(item => ({
                    name: new Date(item.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
                    value: item.total
                  }))}
                  height={200}
                  colors={['#10B981']}
                />
              ) : (
                <div className="h-[200px] flex items-center justify-center text-gray-400">
                  No trend data available
                </div>
              )}
            </CardContent>
          </Card>

          {/* Right: Ticket Trends Bar Chart with Metrics */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base font-semibold">
                Ticket Distribution by Priority
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="grid grid-cols-2 gap-6">
                {/* Left: SLA Compliance List */}
              <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">SLA Compliance</h3>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600 dark:text-gray-400">Resolved</span>
                      <span className="text-sm font-semibold text-gray-900 dark:text-white">{kpis.resolvedTickets || 0}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-green-400"></div>
                        <span className="text-sm text-gray-400 dark:text-gray-300">Open</span>
                </div>
                      <span className="text-sm font-semibold text-gray-700 dark:text-white">{kpis.openTickets || 0}</span>
                </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-orange-500"></div>
                  <span className="text-sm text-gray-600 dark:text-gray-400">Open</span>
                      </div>
                      <span className="text-sm font-semibold text-gray-900 dark:text-white">{kpis.openTickets || 0}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600 dark:text-gray-400">Tickets Today</span>
                      <span className="text-sm font-semibold text-gray-900 dark:text-white">{kpis.ticketsToday || 0}</span>
                    </div>
                  </div>
                </div>

                {/* Right: Donut Chart */}
                <div className="flex items-center justify-center">
                  {ticketTrends?.byPriority && Object.keys(ticketTrends.byPriority).length > 0 ? (
                    <div className="w-full">
                      <Chart
                        type="donut"
                        data={Object.entries(ticketTrends.byPriority)
                          .sort((a, b) => b[1] - a[1])
                          .slice(0, 4)
                          .map(([name, value]) => ({
                            name: name.charAt(0).toUpperCase() + name.slice(1).replace('_', ' '),
                            value: value
                          }))}
                        height={260}
                        colors={['#10B981', '#F59E0B', '#EF4444', '#8B5CF6']}
                      />
                      <div className="mt-4 flex flex-wrap justify-center gap-2 text-xs">
                        {Object.entries(ticketTrends.byPriority)
                          .sort((a, b) => b[1] - a[1])
                          .slice(0, 3)
                          .map(([name, value], idx) => (
                            <div key={idx} className="flex items-center gap-1">
                              <div
                                className="w-1 h-1 rounded"
                                style={{ backgroundColor: ['#3B82F6', '#EF4444', '#6B7280'][idx] }}
                              ></div>
                              <span className="text-gray-600 dark:text-gray-400 capitalize">{name.replace('_', ' ')}</span>
                            </div>
                          ))}
                      </div>
                    </div>
                  ) : (
                    <div className="h-[200px] flex items-center justify-center text-gray-400 text-sm">
                      No priority data
                </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Bottom Row - Charts and Agents */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Left: Ticket Trends Bar Chart with Metrics */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base font-semibold">
                Ticket Trends - Last 30 Days
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="mb-3 grid grid-cols-3 gap-3 text-center">
                <div>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">SLA Compliance</p>
                  <p className="text-sm font-bold text-gray-900 dark:text-white">{kpis.slaCompliance}%</p>
                </div>
                <div>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Avg Resolution Time</p>
                  <p className="text-sm font-bold text-gray-900 dark:text-white">{kpis.avgResolutionTime}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Tickets Today</p>
                  <p className="text-sm font-bold text-gray-900 dark:text-white">{kpis.ticketsToday}</p>
                </div>
              </div>
              {ticketTrends?.timeSeries && ticketTrends.timeSeries.length > 0 ? (
                <Chart
                  type="bar"
                  data={ticketTrends.timeSeries.slice(-7).map(item => ({
                    name: new Date(item.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
                    resolved: item.resolved,
                    open: item.open
                  }))}
                  height={180}
                  colors={['#10B981', '#F59E0B']}
                  dataKeys={[
                    { dataKey: 'resolved', name: 'Resolved' },
                    { dataKey: 'open', name: 'Open' }
                  ]}
                  options={{ legend: false }}
                />
              ) : (
                <div className="h-[180px] flex items-center justify-center text-gray-400">
                  No trend data available
                </div>
              )}
            </CardContent>
          </Card>

          {/* Right: Active Agents */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base font-semibold">
                Active Agents
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              {performanceMetrics?.agentPerformance && performanceMetrics.agentPerformance.length > 0 ? (
                <div className="space-y-3">
                  {performanceMetrics.agentPerformance.slice(0, 5).map((agent, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-700 rounded-lg">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center text-white font-semibold text-sm">
                          {agent.userName ? agent.userName.charAt(0).toUpperCase() : 'A'}
                        </div>
                        <div>
                          <p className="text-xs font-medium text-gray-900 dark:text-white">
                            {agent.userName || `Agent ${agent.userId}`}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {agent.ticketsAssigned} tickets assigned
                          </p>
                </div>
                </div>
                      <div className="text-right">
                        <div className="w-12 h-1.5 bg-gray-200 dark:bg-gray-600 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-green-500 transition-all"
                            style={{ width: `${Math.min(agent.slaCompliance || 0, 100)}%` }}
                          ></div>
                </div>
                        <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                          {agent.slaCompliance?.toFixed(0) || 0}%
                        </p>
                </div>
              </div>
                  ))}
                </div>
              ) : (
                <div className="h-[200px] flex items-center justify-center text-gray-400">
                  No active agents data available
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Drilldown Modal */}
      {drilldownData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 p-6 flex items-center justify-between">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">{drilldownData.title}</h2>
              <button
                onClick={closeDrilldown}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <div className="p-6">
              {drilldownData.data && drilldownData.data.tickets && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between mb-4">
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Total: <span className="font-semibold text-gray-900 dark:text-white">{drilldownData.data.totalCount || drilldownData.data.tickets.length}</span> tickets
                    </p>
                          </div>

                  {/* Tickets Table */}
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr className="border-b border-gray-200 dark:border-gray-700">
                          <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">Ticket #</th>
                          <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">Subject</th>
                          <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">Status</th>
                          <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">Priority</th>
                          <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">Category</th>
                          <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">Department</th>
                          <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">Assignee</th>
                          <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">Requester</th>
                          <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">Created</th>
                          {(selectedKpi === 'resolvedTickets' || selectedKpi === 'closedTickets') && (
                            <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300">Resolved</th>
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {drilldownData.data.tickets.map((ticket, idx) => (
                          <tr
                            key={ticket.ticketId || idx}
                            className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700/50"
                          >
                            <td className="py-3 px-4 text-sm text-gray-900 dark:text-white font-mono">
                              {ticket.ticketNumber || `#${ticket.ticketId}`}
                            </td>
                            <td className="py-3 px-4 text-sm text-gray-900 dark:text-white">
                              <div className="max-w-xs">
                                <div className="font-medium truncate">{ticket.subject || 'No Subject'}</div>
                                {ticket.description && (
                                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">
                                    {ticket.description.length > 100
                                      ? ticket.description.substring(0, 100) + '...'
                                      : ticket.description}
                </div>
              )}
                                {(ticket.countryName || ticket.countyName || ticket.subCountyName || ticket.wardName) && (
                                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                    📍 {[
                                      ticket.countryName,
                                      ticket.countyName,
                                      ticket.subCountyName,
                                      ticket.wardName
                                    ].filter(Boolean).join(' > ')}
                </div>
              )}
                          </div>
                            </td>
                            <td className="py-3 px-4 text-sm">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold capitalize ${getStatusBadgeColor(ticket.statusCode)}`}>
                                {ticket.statusName || ticket.statusCode || 'N/A'}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-sm">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold capitalize ${getPriorityBadgeColor(ticket.priorityCode)}`}>
                                {ticket.priorityName || ticket.priorityCode || 'N/A'}
                            </span>
                            </td>
                            <td className="py-3 px-4 text-sm text-gray-700 dark:text-gray-300">
                              {ticket.categoryName || 'Uncategorized'}
                            </td>
                            <td className="py-3 px-4 text-sm text-gray-700 dark:text-gray-300">
                              {ticket.departmentName || 'Unassigned'}
                            </td>
                            <td className="py-3 px-4 text-sm text-gray-700 dark:text-gray-300">
                              {ticket.assigneeName || (ticket.assigneeId ? `User ${ticket.assigneeId}` : 'Unassigned')}
                            </td>
                            <td className="py-3 px-4 text-sm text-gray-700 dark:text-gray-300">
                              {ticket.requesterName || (ticket.requesterId ? `User ${ticket.requesterId}` : 'N/A')}
                            </td>
                            <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400">
                              {ticket.createdAt
                                ? new Date(ticket.createdAt).toLocaleDateString('en-US', {
                                  year: 'numeric',
                                  month: 'short',
                                  day: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })
                                : 'N/A'}
                            </td>
                            {selectedKpi === 'resolvedTickets' && (
                              <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400">
                                {ticket.resolvedAt
                                  ? new Date(ticket.resolvedAt).toLocaleDateString('en-US', {
                                    year: 'numeric',
                                    month: 'short',
                                    day: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })
                                  : 'N/A'}
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>

                    {drilldownData.data.tickets.length === 0 && (
                      <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                        No tickets found
                    </div>
              )}
                  </div>
                </div>
              )}

              {selectedKpi === 'slaCompliance' && drilldownData.data && (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="p-6 bg-green-50 dark:bg-green-900/20 rounded-lg text-center">
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">SLA Met</p>
                      <p className="text-3xl font-bold text-green-600 dark:text-green-400">{drilldownData.data.met || 0}</p>
                    </div>
                    <div className="p-6 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg text-center">
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">SLA Warning</p>
                      <p className="text-3xl font-bold text-yellow-600 dark:text-yellow-400">{drilldownData.data.warning || 0}</p>
                    </div>
                    <div className="p-6 bg-red-50 dark:bg-red-900/20 rounded-lg text-center">
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">SLA Breached</p>
                      <p className="text-3xl font-bold text-red-600 dark:text-red-400">{drilldownData.data.breached || 0}</p>
                    </div>
                  </div>
                  <div className="p-4 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg text-center">
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">Overall Compliance Rate</p>
                    <p className="text-4xl font-bold text-indigo-600 dark:text-indigo-400">{drilldownData.data.complianceRate || kpis.slaCompliance}%</p>
                  </div>
                  {drilldownData.data.slaByStatus && drilldownData.data.slaByStatus.length > 0 && (
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">SLA Compliance by Status</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {drilldownData.data.slaByStatus.map((item, idx) => (
                          <div key={idx} className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                            <div className="flex items-center justify-between">
                              <span className="text-sm font-medium text-gray-700 dark:text-gray-300 capitalize">{item.status}</span>
                              <span className="text-lg font-bold text-gray-900 dark:text-white">{item.compliance?.toFixed(1) || 0}%</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {selectedKpi === 'avgResolutionTime' && drilldownData.data && (
                <div className="space-y-6">
                  {drilldownData.data.byPriority && drilldownData.data.byPriority.length > 0 && (
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Resolution Time by Priority</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {drilldownData.data.byPriority.map((item, idx) => (
                          <div key={idx} className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                            <div className="flex items-center justify-between">
                              <span className="text-sm font-medium text-gray-700 dark:text-gray-300 capitalize">{item.priority}</span>
                              <span className="text-lg font-bold text-gray-900 dark:text-white">{item.time || 'N/A'}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {drilldownData.data.byCategory && drilldownData.data.byCategory.length > 0 && (
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Resolution Time by Category</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {drilldownData.data.byCategory.map((item, idx) => (
                          <div key={idx} className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                            <div className="flex items-center justify-between">
                              <span className="text-sm font-medium text-gray-700 dark:text-gray-300 capitalize">{item.category}</span>
                              <span className="text-lg font-bold text-gray-900 dark:text-white">{item.time || 'N/A'}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {selectedKpi === 'activeAgents' && drilldownData.data && drilldownData.data.agents && (
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Agent Performance</h3>
                  <div className="space-y-2">
                    {drilldownData.data.agents.map((agent, idx) => (
                      <div key={idx} className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1">
                            <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-1">{agent.userName || `Agent ${agent.userId}`}</h4>
                            <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
                              <span>Tickets Assigned: {agent.ticketsAssigned || 0}</span>
                              <span>Tickets Resolved: {agent.ticketsResolved || 0}</span>
                              <span>Avg Resolution: {agent.avgResolutionTime || 'N/A'}</span>
                              <span>SLA Compliance: {agent.slaCompliance?.toFixed(1) || 0}%</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PublicDashboard;

